import { create } from "zustand";
import { supabase } from "../lib/supabase";
import { Database } from "../lib/database";

type ListPlace = Database["public"]["Tables"]["list_places"]["Row"];
type Place = Database["public"]["Tables"]["places"]["Row"];

interface PlaceState {
  currentPlace: Place | null;
  listPlaces: ListPlace[];
  places: Place[];
  placesLoading: boolean;
  error: string | null;

  // Places management
  fetchListPlaces: (listId: string) => Promise<void>;
  fetchPlacesForList: (listId: string) => Promise<Place[]>;
  fetchPlace: (params: {
    userId: string;
    lat?: number;
    lng?: number;
    placeId?: string | null;
    address?: string;
  }) => Promise<Place | null>;
  createPlace: (
    place: Database["public"]["Tables"]["places"]["Insert"],
  ) => Promise<Place>;
  updatePlace: (
    id: string,
    updates: Database["public"]["Tables"]["places"]["Update"],
  ) => Promise<void>;
  deletePlace: (id: string) => Promise<void>;
  setCurrentPlace: (place: Place | null) => void;

  // ListPlace management
  addPlaceToList: (
    place: Database["public"]["Tables"]["places"]["Insert"],
    listId: string,
  ) => Promise<void>;
  deleteListPlace: (listPlaceId: string) => Promise<void>;

  // Fetch place by ID (for viewing other users' places)
  fetchPlaceById: (placeId: string) => Promise<Place | null>;
  findPlaceByGoogleId: (userId: string, googlePlaceId: string) => Promise<Place | null>;

  clearError: () => void;
}

export const usePlaceStore = create<PlaceState>((set) => ({
  currentPlace: null,
  places: [],
  listPlaces: [],
  placesLoading: false,
  error: null,

  fetchListPlaces: async (listId) => {
    try {
      set({ placesLoading: true, error: null });

      const { data, error } = await supabase
        .from("list_places")
        .select("*")
        .eq("list_id", listId)
        .order("visit_date", { ascending: true });

      if (error) throw error;

      set({ listPlaces: data || [] });
    } catch (error) {
      const message = "Failed to fetch places";
      console.error(message, error);
      set({ error: message });
    } finally {
      set({ placesLoading: false });
    }
  },

  fetchPlacesForList: async (listId) => {
    try {
      set({ placesLoading: true, error: null });

      // First fetch list_places to get place_ids
      const { data: listPlacesData, error: lpError } = await supabase
        .from("list_places")
        .select("place_id")
        .eq("list_id", listId);

      if (lpError) throw lpError;

      if (!listPlacesData || listPlacesData.length === 0) {
        return [];
      }

      // Extract unique place_ids (filter out nulls)
      const placeIds = listPlacesData
        .map((lp) => lp.place_id)
        .filter((id): id is string => id !== null);

      if (placeIds.length === 0) {
        return [];
      }

      // Fetch actual places
      const { data: placesData, error: placesError } = await supabase
        .from("places")
        .select("*")
        .in("id", placeIds);

      if (placesError) throw placesError;

      return placesData || [];
    } catch (error) {
      const message = "Failed to fetch places for list";
      console.error(message, error);
      set({ error: message });
      return [];
    } finally {
      set({ placesLoading: false });
    }
  },

  fetchPlace: async ({ userId, lat, lng, placeId, address }) => {
    try {
      set({ placesLoading: true, error: null });
      // If placeId provided, fetch directly
      if (placeId) {
        const { data, error } = await supabase
          .from("places")
          .select("*")
          .eq("google_place_id", placeId)
          .eq("user_id", userId)
          .maybeSingle();

        if (error) throw error;
        set({ currentPlace: data || null });
        return data || null;
      }

      // If lat/lng provided, search within a small bounding box (0.01 deg ~ ~1km)
      if (typeof lat === "number" && typeof lng === "number") {
        const latMin = lat - 0.00001;
        const latMax = lat + 0.00001;
        const lngMin = lng - 0.00001;
        const lngMax = lng + 0.00001;

        const { data, error } = await supabase
          .from("places")
          .select("*")
          .gte("latitude", latMin)
          .lte("latitude", latMax)
          .gte("longitude", lngMin)
          .lte("longitude", lngMax)
          .eq("user_id", userId)
          .limit(1);

        if (error) throw error;
        const place = data && data.length > 0 ? data[0] : null;
        set({ currentPlace: place });
        return place;
      }

      // If address provided, attempt an ilike match
      if (address) {
        const { data, error } = await supabase
          .from("places")
          .select("*")
          .ilike("address", `%${address}%`)
          .eq("user_id", userId)
          .limit(1);

        if (error) throw error;
        const place = data && data.length > 0 ? data[0] : null;
        set({ currentPlace: place });
        return place;
      }

      return null;
    } catch (error) {
      const message = "Failed to fetch place";
      console.error(message, error);
      set({ error: message });
      return null;
    } finally {
      set({ placesLoading: false });
    }
  },

  createPlace: async (place) => {
    try {
      set({ placesLoading: true, error: null });

      const { data, error } = await supabase
        .from("places")
        .insert(place)
        .select()
        .single();

      if (error) throw error;

      set((state) => ({ places: [...state.places, data] }));
      return data;
    } catch (error) {
      const message = "Failed to create place";
      console.error(message, error);
      set({ error: message });
      throw error;
    } finally {
      set({ placesLoading: false });
    }
  },

  updatePlace: async (id, updates) => {
    try {
      set({ placesLoading: true, error: null });

      const { data, error } = await supabase
        .from("places")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;

      set((state) => ({
        places: state.places.map((place) => (place.id === id ? data : place)),
      }));
    } catch (error) {
      const message = "Failed to update place";
      console.error(message, error);
      set({ error: message });
      throw error;
    } finally {
      set({ placesLoading: false });
    }
  },

  deletePlace: async (id) => {
    try {
      set({ placesLoading: true, error: null });

      const { error } = await supabase.from("places").delete().eq("id", id);

      if (error) throw error;

      set((state) => ({
        places: state.places.filter((place) => place.id !== id),
      }));
    } catch (error) {
      const message = "Failed to delete place";
      console.error(message, error);
      set({ error: message });
      throw error;
    } finally {
      set({ placesLoading: false });
    }
  },

  addPlaceToList: async (place, listId) => {
    try {
      set({ placesLoading: true, error: null });

      const placeToInsert = {
        user_id: place.user_id,
        place_id: place.id,
        address: place.address,
        place_name: place.name,
        latitude: place.latitude,
        longitude: place.longitude,
        category: place.category,
        google_place_id: place.google_place_id,
        country: place.country,
        list_id: listId,
      };

      const { data, error } = await supabase
        .from("list_places")
        .insert(placeToInsert)
        .select()
        .single();

      if (error) throw error;

      set((state) => ({
        places: [...state.places, data],
      }));
    } catch (error) {
      const message = "Failed to add place to list";
      console.error(message, error);
      set({ error: message });
      throw error;
    } finally {
      set({ placesLoading: false });
    }
  },

  deleteListPlace: async (listPlaceId) => {
    const { error } = await supabase
      .from("list_places")
      .delete()
      .eq("id", listPlaceId);
    if (error) throw error;
    set((state) => ({
      listPlaces: state.listPlaces.filter((lp) => lp.id !== listPlaceId),
    }));
  },

  setCurrentPlace: (place) => {
    set({ currentPlace: place });
  },

  fetchPlaceById: async (placeId) => {
    try {
      const { data, error } = await supabase
        .from("places")
        .select("*")
        .eq("id", placeId)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error("Error fetching place by ID:", error);
      return null;
    }
  },

  findPlaceByGoogleId: async (userId, googlePlaceId) => {
    try {
      const { data, error } = await supabase
        .from("places")
        .select("*")
        .eq("user_id", userId)
        .eq("google_place_id", googlePlaceId)
        .maybeSingle();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error("Error finding place by Google ID:", error);
      return null;
    }
  },

  clearError: () => set({ error: null }),
}));
